import React from 'react';
import styled from 'styled-components';
import { DraggableRowsTable } from '@components/draggable-rows-table';
import { Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import { CompanyTypePicker } from './company-type-picker-v2';
import { Column } from 'react-table';
import { Option } from './company-type-editor-v2';
import { useCallback, useMemo} from 'react';
import { createFormField } from '../../common';
import { createContextQuick } from '@components/quick-context';
import { ColoredChipIconButton, ColoredChip } from '@components/colored-elements';
import { BiEdit, ImDrawer2, TrashFill } from '@icon/react-icons';
import { useSnackbarInstance } from '@lib/common';
import { red, teal, blue } from '@material-ui/core/colors';
import { thumbStack } from '@lib/common/css';
import { valuesIn } from 'lodash';

const [ProvideEdit, useActions] = createContextQuick<{
  onEdit?: (tuple: Option[], idx?: number) => any;
  onDelete?: (idx: number) => any;
}>();

export const CompanyTypeEditor = ({
  value,
  onChange,
  isLock=true
}: {
  value: Option[];
  onChange: (value: Option[]) => any;
  isLock:boolean;
}) => {
  const getRowId = React.useCallback((row: Option, idx) => {
    // return '' + row.type.value + row.subtype?.value;
    return '' + row.value + idx;
  }, []);

  const { enqueue } = useSnackbarInstance();

  const onDelete = useCallback(
    (idx: number) => {
      if (value) {
        if (value[idx]) {
          const next = [...value];
          next.splice(idx, 1);
          onChange(next);
        } else {
          enqueue(
            <>
              Report this error,
              <br /> No type available to delete.
            </>
          );
        }
      }
    },
    [value, onChange, enqueue]
  );

  const onAddOrEdit = useCallback(
    (v: Option[], idx?: number) => {
      onChange(v);
    },
    [value, onChange, enqueue]
  );

  const columns: Column<Option>[] = useMemo (
    () => [
    {
      Header: '#',
      id: 'serial',
      accessor: (_row, idx) => idx + 1,
      width: 35,
    },
    {
      Header: 'Type',
      id: 'type',
      accessor: row => row.label,
      width: 200,
    },
    {
      Header: 'Status',
      id: 'status',
      // /accessor: (_row, idx) => idx + 1,
      accessor: (_row, idx) => {
      
        if (idx + 1 == 1) {
          return (
            <Tooltip title="Primary Service">
              <span>
                <ColoredChip colored={blue[700]} label="Primary" />
              </span>
            </Tooltip>
          );
        } else {
          return (
            <>
              <Tooltip title="Secondary Services">
                <span>
                  <ColoredChip colored={teal[700]} label="Secondary" />
                </span>
              </Tooltip>
            </>
          );
        }
      },
      width: 55,
    },
    {
      Header: 'Action',
      id: 'action',
      width: 80,
      Cell: ({ row }) => {
        const { onEdit, onDelete } = useActions();
  
        const onDeleteClick = useCallback(() => onDelete(row.index), [onDelete, row.index]);
  
        return (
          <>
            <Tooltip title="Delete">
              <span>
                <ColoredChipIconButton
                  onClick={onDeleteClick}
                  disabled={isLock}
                  pointer
                  colored={red[900]}
                  size="small"
                  icon={<TrashFill />}
                />
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ],[isLock]
  );
  
  return (
    <TypesContainer>
      <ProvideEdit onEdit={onAddOrEdit} onDelete={onDelete}>
        <Grid container spacing={4} justify="center" alignItems="center">
          <Grid item>
            <Typography variant="h6" style={{ fontSize: 16 }}>
              Manage Company Types *
            </Typography>
          </Grid>
          <Grid item xs></Grid>
          <Grid item>
            <CompanyTypePicker
              onDone={onAddOrEdit}
              variant="contained"
              color="primary"
              clearOnDone
              style={{ marginRight: 4 }}
              categoryTuple={value}
              isLock={isLock}
            />
          </Grid>
        </Grid>
        {value ? (
          <Paper className="fixed-height">
            <DraggableRowsTable
              getRowId={getRowId}
              onChange={onChange}
              columns={columns}
              data={value}
              stickyHeader
              size="small"
            />
          </Paper>
        ) : (
          <Paper className="fixed-height no-data">
            <span>
              <ImDrawer2 />
              <br />
              <Typography>Click on Add button to add a type.</Typography>
            </span>
          </Paper>
        )}
      </ProvideEdit>
    </TypesContainer>
  );
};

// export const CompanyCategoriesField = createFormField(CompanyTypeEditor);

export const CompanyCategoriesField = createFormField(CompanyTypeEditor, {
  defaultValue: [],
  passProps: true,
});


const ChipRemovedChildren = ({ children, ...props }) => <ColoredChipIconButton {...props} />;

export const TypesContainer = styled.div`
  padding: 10px 5px;
  background: #f5f5f5;
  table {
    td:nth-of-type(1) {
      width: 20px;
    }
    td:nth-of-type(2) {
      width: 35px;
    }
    td:last-child {
      width: 80px;
    }
  }
  .drag-icon {
    cursor: grab;
  }
  .fixed-height {
    ${thumbStack}
    margin-top: 6px;
    margin-bottom: 6px;
    height: 40vh;
    position: relative;
    overflow: auto;
    .MuiTableContainer-root {
      ${thumbStack}
      height: inherit;
      overflow: inherit;
    }
    &.no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      span {
        text-align: center;
        svg {
          font-size: 4em;
          color: #ccc;
        }
      }
    }
  }
`;
